/* -------------------------------------------------------
* Types
* ----------------------------------------------------- */
type FirestoreLikeTimestamp = {
  seconds: number;
  nanoseconds?: number;
 };
 
 
 /* -------------------------------------------------------
 * Convert milliseconds → Firestore Timestamp
 * ----------------------------------------------------- */
 function convertMsToTimestamp(milliseconds: number | string): FirestoreLikeTimestamp {
  const ms = Number(milliseconds);
 
 
  const seconds = Math.floor(ms / 1000);
  const remainingMs = ms % 1000;
 
 
  return {
    seconds,
    nanoseconds: remainingMs * 1_000_000,
  };
 }
 
 
 /* -------------------------------------------------------
 * Normalize ANY date value → Firestore-like timestamp
 * ----------------------------------------------------- */
 function normalizeToTimestamp(value: any): FirestoreLikeTimestamp | null {
  if (!value) return null;
 
 
  // Already Firestore Timestamp
  if (typeof value === "object" && typeof value.seconds === "number") {
    return value;
  }
 
 
  const num = Number(value);
  if (!isNaN(num)) {
    // milliseconds (13 digits)
    if (num > 1e12) {
      return convertMsToTimestamp(num);
    }
 
 
    // seconds
    return {
      seconds: num,
      nanoseconds: 0,
    };
  }
 
 
  return null;
 }
 
 
 /* -------------------------------------------------------
 * Format time difference (X ago)
 * ----------------------------------------------------- */
 export function formatTimeDifference(value: any): string | null {
  const ts = normalizeToTimestamp(value);
  if (!ts?.seconds) return null;
 
 
  const now = Date.now();
  const then = ts.seconds * 1000;
 
 
  const diffMs = now - then;
  if (diffMs < 0) return null;
 
 
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
 
 
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  if (months < 12) return `${months}m ago`;
 
 
  return `${years}y ago`;
 }
 
 
 /* -------------------------------------------------------
 * Convert timestamp → ISO string
 * ----------------------------------------------------- */
 function toISO(value: any): string | null {
  const ts = normalizeToTimestamp(value);
  if (!ts) return null;
  return new Date(ts.seconds * 1000).toISOString();
 }
 
 
 /* -------------------------------------------------------
 * 24h → 12h time formatter
 * ----------------------------------------------------- */
 export function to12Hour(time24: string): string {
  if (!time24) return "";
 
 
  let [hours, minutes] = time24.split(":").map(Number);
  const ampm = hours >= 12 ? "PM" : "AM";
 
 
  hours = hours % 12 || 12;
 
 
  return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
 }

 function to12HourFromParts(hours24: number, minutes: number): string {
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;

  return `${hours12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
 }

 function formatWebinarTimeRange(
  startHours: number,
  startMinutes: number,
  endHours: number,
  endMinutes: number,
 ): string {
  return `${to12HourFromParts(startHours, startMinutes)} - ${to12HourFromParts(endHours, endMinutes)}`;
 }

 function parseWistiaScheduleFields(scheduledFor?: string | null) {
  if (!scheduledFor) return null;

  const match = scheduledFor.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
    hours: Number(match[4]),
    minutes: Number(match[5]),
  };
 }

 function extractWistiaEventDate(item: any) {
  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];

  const schedule = parseWistiaScheduleFields(item?.wistiaScheduledFor);
  if (schedule) {
    return {
      monthYear: `${months[schedule.month]} ${schedule.year}`,
      days: `${schedule.day}`,
    };
  }

  const startTs = normalizeToTimestamp(item?.eventStartDate);
  if (!startTs) return { monthYear: null, days: null };

  const start = new Date(startTs.seconds * 1000);
  return {
    monthYear: `${months[start.getUTCMonth()]} ${start.getUTCFullYear()}`,
    days: `${start.getUTCDate()}`,
  };
 }

 function formatWebinarEventTime(item: any): string | null {
  const durationSeconds = item?.wistiaEventDuration;
  const schedule = parseWistiaScheduleFields(item?.wistiaScheduledFor);

  if (schedule) {
    if (durationSeconds) {
      const startTotalMinutes = schedule.hours * 60 + schedule.minutes;
      const endTotalMinutes = startTotalMinutes + Math.floor(durationSeconds / 60);
      const endHours = Math.floor(endTotalMinutes / 60) % 24;
      const endMinutes = endTotalMinutes % 60;

      return formatWebinarTimeRange(
        schedule.hours,
        schedule.minutes,
        endHours,
        endMinutes,
      );
    }

    return to12HourFromParts(schedule.hours, schedule.minutes);
  }

  const startTs = normalizeToTimestamp(item?.eventStartDate);
  if (!startTs) {
    return item?.eventStartTime ? to12Hour(item.eventStartTime) : null;
  }

  const start = new Date(startTs.seconds * 1000);
  const startHours = start.getUTCHours();
  const startMinutes = start.getUTCMinutes();

  if (durationSeconds) {
    const end = new Date(start.getTime() + durationSeconds * 1000);
    return formatWebinarTimeRange(
      startHours,
      startMinutes,
      end.getUTCHours(),
      end.getUTCMinutes(),
    );
  }

  return to12HourFromParts(startHours, startMinutes);
 }
 
 
 /* -------------------------------------------------------
 * Sort by latest created_at
 * ----------------------------------------------------- */
 export function filterByLatest(data: any[] = []) {
  return [...data].sort((a, b) => {
    const aTs = normalizeToTimestamp(a?.created_at);
    const bTs = normalizeToTimestamp(b?.created_at);
 
 
    if (!aTs || !bTs) return 0;
    return bTs.seconds - aTs.seconds;
  });
 }
 
 
 /* -------------------------------------------------------
 * Active feed: Wistia webinars newest-first, then manual
 * events by start date, then posts by created_at desc.
 * ----------------------------------------------------- */
 export function sortActiveFeed(data: any[] = []) {
  return [...data].sort((a, b) => {
    const aIsWistiaWebinar = a?.source === "wistia" && !!a?.wistiaWebinarId;
    const bIsWistiaWebinar = b?.source === "wistia" && !!b?.wistiaWebinarId;

    if (aIsWistiaWebinar && bIsWistiaWebinar) {
      const aCr = normalizeToTimestamp(a?.created_at);
      const bCr = normalizeToTimestamp(b?.created_at);
      if (aCr && bCr) return bCr.seconds - aCr.seconds;
      return 0;
    }

    if (aIsWistiaWebinar && !bIsWistiaWebinar) return -1;
    if (!aIsWistiaWebinar && bIsWistiaWebinar) return 1;

    const aTs = normalizeToTimestamp(a?.eventStartDate);
    const bTs = normalizeToTimestamp(b?.eventStartDate);

    if (aTs && bTs) return aTs.seconds - bTs.seconds;
    if (aTs && !bTs) return -1;
    if (!aTs && bTs) return 1;

    const aCr = normalizeToTimestamp(a?.created_at);
    const bCr = normalizeToTimestamp(b?.created_at);
    if (aCr && bCr) return bCr.seconds - aCr.seconds;
    return 0;
  });
 }


 /* -------------------------------------------------------
 * Sort by eventStartDate (nearest date first).
 * Posts (no eventStartDate) are placed after events,
 * sorted among themselves by created_at (latest first).
 * Pass direction="desc" for past events (most recent first).
 * ----------------------------------------------------- */
 export function sortByEventStartDate(
  data: any[] = [],
  direction: "asc" | "desc" = "asc"
 ) {
  return [...data].sort((a, b) => {
    const aTs = normalizeToTimestamp(a?.eventStartDate);
    const bTs = normalizeToTimestamp(b?.eventStartDate);
 
 
    // Both have eventStartDate → compare them
    if (aTs && bTs) {
      return direction === "asc"
        ? aTs.seconds - bTs.seconds
        : bTs.seconds - aTs.seconds;
    }
 
 
    // Only one has eventStartDate → events come first
    if (aTs && !bTs) return -1;
    if (!aTs && bTs) return 1;
 
 
    // Neither has eventStartDate (posts) → sort by created_at desc
    const aCr = normalizeToTimestamp(a?.created_at);
    const bCr = normalizeToTimestamp(b?.created_at);
    if (aCr && bCr) return bCr.seconds - aCr.seconds;
    return 0;
  });
 }
 
 
 /* -------------------------------------------------------
 * Convert posts (ADMIN + USER SAFE)
 * ----------------------------------------------------- */
 export function convertPosts(data: any[] = []) {
 
 
  const finalResults = data.map((item: any) => {
    /* -------- Event Date Extraction -------- */
    const extractEventDate = () => {
      if (item?.post_type !== "event") {
        return { monthYear: null, days: null };
      }

      if (item?.wistiaWebinarId) {
        return extractWistiaEventDate(item);
      }

      const startTs = normalizeToTimestamp(item?.eventStartDate);
      if (!startTs) return { monthYear: null, days: null };
 
 
      const start = new Date(startTs.seconds * 1000);
 
 
      const endTs = normalizeToTimestamp(item?.eventEndDate);
      const end = endTs ? new Date(endTs.seconds * 1000) : null;
 
 
      const months = [
        "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
        "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
      ];
 
 
      const monthYear =
        end && start.getMonth() !== end.getMonth()
          ? `${months[start.getMonth()]}-${months[end.getMonth()]} ${start.getFullYear()}`
          : `${months[start.getMonth()]} ${start.getFullYear()}`;
 
 
      const days =
        !end || start.getDate() === end.getDate()
          ? `${start.getDate()}`
          : `${start.getDate()}-${end.getDate()}`;
 
 
      return { monthYear, days };
    };
 
 
    /* -------- Normalized Post -------- */
    return {
      id: item?.id || item?.objectID || null,
      title: item?.title ?? null,
 
 
      lastUpdated: formatTimeDifference(item?.created_at),
 
 
      created_at: toISO(item?.created_at),
      updated_at: toISO(item?.updated_at),
 
 
      created_by: item?.created_by ?? null,
      updated_by: item?.updated_by ?? null,
 
 
      featured: item?.featured ?? false,
      isFeatured:
        item?.isFeatuered === true,
 
 
      status: item?.status ?? null,
      description: item?.description ?? null,
 
 
      date: extractEventDate(),
 
 
      time:
        item?.post_type === "event" && item?.eventStartTime
          ? item?.wistiaWebinarId
            ? formatWebinarEventTime(item)
            : to12Hour(item.eventStartTime)
          : null,
 
 
      location: item?.eventLocation ?? null,
      image: (() => {
        const images = Array.isArray(item?.images) ? item.images.filter(Boolean) : [];
        if (images.length) return images;
        if (item?.wistiaThumbnailUrl) return [item.wistiaThumbnailUrl];
        return [];
      })(),
      link: item?.eventUrl ?? null,


      post_type: item?.post_type ?? null,
      source: item?.source ?? "manual",
      eventbriteId: item?.eventbriteId ?? null,
      wistiaMediaId: item?.wistiaMediaId ?? null,
      wistiaWebinarId: item?.wistiaWebinarId ?? null,
      wistiaMediaUrl: item?.wistiaMediaUrl ?? null,
      wistiaThumbnailUrl: item?.wistiaThumbnailUrl ?? null,
      wistiaDuration: item?.wistiaDuration ?? null,
    };
  });


  const filterFeautred = finalResults.filter(item => item.isFeatured);
  const nonFeautred = finalResults.filter(item => !item?.isFeatured);


  return [...filterFeautred, ...nonFeautred];
 }
 
 
 /* -------------------------------------------------------
 * Firestore timestamp → Date
 * ----------------------------------------------------- */
 export function firestoreTimestampToDate_event(value: any): Date | null {
  const ts = normalizeToTimestamp(value);
  if (!ts) return null;
  return new Date(ts.seconds * 1000);
 }
 
 
 const getPaginationRange = (
  currentPage: number,
  totalPages: number
 ): (number | string)[] => {
  const delta = 1;
  const range: (number | string)[] = [];
  const rangeWithDots: (number | string)[] = [];
  let lastPage: number | undefined;
 
 
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    }
  }
 
 
  for (const page of range) {
    if (lastPage) {
      if ((page as number) - lastPage === 2) {
        rangeWithDots.push(lastPage + 1);
      } else if ((page as number) - lastPage > 2) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(page);
    lastPage = page as number;
  }
 
 
  return rangeWithDots;
 };
 